// src/pages/onboarding/AssessmentPage.tsx - Identity assessment page

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Container, 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper,
  Stepper,
  Step,
  StepLabel,
  CircularProgress
} from '@mui/material';
import { createProfile, setAssessmentComplete } from '../../store/slices/profileSlice';
import { AppDispatch, RootState } from '../../store';
import { useAuth } from '../../contexts/AuthContext';

const steps = [
  'Core Values',
  'Pain Points',
  'Life Themes',
  'Purpose',
  'Goals'
];

const AssessmentPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { currentUser } = useAuth();
  const { isLoading } = useSelector((state: RootState) => state.profile);
  
  const [activeStep, setActiveStep] = useState(0);
  const [coreValues, setCoreValues] = useState<string[]>(['']);
  const [painPoints, setPainPoints] = useState<string[]>(['']);
  const [lifeThemes, setLifeThemes] = useState<string[]>(['']);
  const [purpose, setPurpose] = useState('');
  const [goals, setGoals] = useState<string[]>(['']);
  
  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };
  
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };
  
  const handleAddField = (type: 'values' | 'painPoints' | 'themes' | 'goals') => {
    switch (type) {
      case 'values':
        setCoreValues([...coreValues, '']);
        break;
      case 'painPoints':
        setPainPoints([...painPoints, '']);
        break;
      case 'themes':
        setLifeThemes([...lifeThemes, '']);
        break;
      case 'goals':
        setGoals([...goals, '']);
        break;
    }
  };
  
  const handleFieldChange = (
    type: 'values' | 'painPoints' | 'themes' | 'goals',
    index: number,
    value: string
  ) => {
    switch (type) {
      case 'values':
        const newValues = [...coreValues];
        newValues[index] = value;
        setCoreValues(newValues);
        break;
      case 'painPoints':
        const newPainPoints = [...painPoints];
        newPainPoints[index] = value;
        setPainPoints(newPainPoints);
        break;
      case 'themes':
        const newThemes = [...lifeThemes];
        newThemes[index] = value;
        setLifeThemes(newThemes);
        break;
      case 'goals':
        const newGoals = [...goals];
        newGoals[index] = value;
        setGoals(newGoals);
        break;
    }
  };
  
  const handleComplete = async () => {
    if (!currentUser) return;
    
    // Filter out empty values
    const filteredValues = coreValues.filter(value => value.trim() !== '');
    const filteredPainPoints = painPoints.filter(point => point.trim() !== '');
    const filteredThemes = lifeThemes.filter(theme => theme.trim() !== '');
    const filteredGoals = goals.filter(goal => goal.trim() !== '');
    
    const profileData = {
      userId: currentUser.uid,
      coreValues: filteredValues,
      painPoints: filteredPainPoints,
      lifeThemes: filteredThemes,
      customFields: {
        purpose,
        goals: filteredGoals,
      },
    };
    
    const result = await dispatch(createProfile(profileData));
    
    if (createProfile.fulfilled.match(result)) {
      dispatch(setAssessmentComplete(true));
      navigate('/dashboard');
    }
  };
  
  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <>
            <Typography variant="h6" sx={{ mb: 2 }}>
              What are your core values?
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Identify the principles that guide your decisions and actions. These are the foundations of your character and identity.
            </Typography>
            
            {coreValues.map((value, index) => (
              <TextField
                key={index}
                fullWidth
                label={`Core Value ${index + 1}`}
                placeholder="e.g., Integrity, Courage, Excellence"
                value={value}
                onChange={(e) => handleFieldChange('values', index, e.target.value)}
                margin="normal"
              />
            ))}
            
            <Button
              variant="outlined"
              onClick={() => handleAddField('values')}
              sx={{ mt: 2 }}
            >
              Add Another Value
            </Button>
          </>
        );
      case 1:
        return (
          <>
            <Typography variant="h6" sx={{ mb: 2 }}>
              What are your pain points?
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Identify the challenges, weaknesses, or struggles that hold you back from reaching your potential.
            </Typography>
            
            {painPoints.map((point, index) => (
              <TextField
                key={index}
                fullWidth
                label={`Pain Point ${index + 1}`}
                placeholder="e.g., Procrastination, Anger, Lack of discipline"
                value={point}
                onChange={(e) => handleFieldChange('painPoints', index, e.target.value)}
                margin="normal"
              />
            ))}
            
            <Button
              variant="outlined"
              onClick={() => handleAddField('painPoints')}
              sx={{ mt: 2 }}
            >
              Add Another Pain Point
            </Button>
          </>
        );
      case 2:
        return (
          <>
            <Typography variant="h6" sx={{ mb: 2 }}>
              What are your life themes?
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Identify recurring patterns, lessons, or motifs that have shaped your journey and perspective.
            </Typography>
            
            {lifeThemes.map((theme, index) => (
              <TextField
                key={index}
                fullWidth
                label={`Life Theme ${index + 1}`}
                placeholder="e.g., Resilience, Growth through adversity"
                value={theme}
                onChange={(e) => handleFieldChange('themes', index, e.target.value)}
                margin="normal"
              />
            ))}
            
            <Button
              variant="outlined"
              onClick={() => handleAddField('themes')}
              sx={{ mt: 2 }}
            >
              Add Another Theme
            </Button>
          </>
        );
      case 3:
        return (
          <>
            <Typography variant="h6" sx={{ mb: 2 }}>
              What is your purpose?
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Describe your sense of meaning and direction. What drives you beyond immediate goals?
            </Typography>
            
            <TextField
              fullWidth
              label="Purpose Statement"
              placeholder="e.g., To build systems that empower others to reach their potential"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              margin="normal"
              multiline
              rows={4}
            />
          </>
        );
      case 4:
        return (
          <>
            <Typography variant="h6" sx={{ mb: 2 }}>
              What are your key goals?
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Identify specific objectives you're working toward in different areas of your life.
            </Typography>
            
            {goals.map((goal, index) => (
              <TextField
                key={index}
                fullWidth
                label={`Goal ${index + 1}`}
                placeholder="e.g., Build a sustainable business, Master a martial art"
                value={goal}
                onChange={(e) => handleFieldChange('goals', index, e.target.value)}
                margin="normal"
              />
            ))}
            
            <Button
              variant="outlined"
              onClick={() => handleAddField('goals')}
              sx={{ mt: 2 }}
            >
              Add Another Goal
            </Button>
          </>
        );
      default:
        return null;
    }
  };
  
  return (
    <Container component="main" maxWidth="md">
      <Box
        sx={{
          marginTop: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography component="h1" variant="h4" sx={{ mb: 4, fontWeight: 'bold' }}>
          Identity Assessment
        </Typography>
        
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          
          <Box sx={{ mb: 4 }}>
            {renderStepContent(activeStep)}
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              variant="outlined"
              onClick={handleBack}
              disabled={activeStep === 0 || isLoading}
            >
              Back
            </Button>
            
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleComplete}
                disabled={isLoading}
              >
                {isLoading ? <CircularProgress size={24} /> : 'Complete'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={isLoading}
              >
                Next
              </Button>
            )}
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default AssessmentPage;
